'use client'
import { RiArrowRightSLine, RiArrowRightUpLine } from '@remixicon/react'
import { useEffect, useRef, useState } from 'react'
import ButtonWrapper from '../button-wrapper'

export function Accordion({
  icon,
  title,
  summary,
  actionText = 'View document',
  actionLink = '/cancellation-policy.pdf',
  children,
  defaultOpen = false,
  showDivider = true,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const panelRef = useRef(null)
  const panelId = `accordion-panel-${Math.random().toString(36).substring(2, 9)}`

  // Auto-measure max-height for smooth transition with variable content  
  useEffect(() => {
    const el = panelRef.current
    if (!el) return

    if (isOpen) {
      el.style.maxHeight = el.scrollHeight + 'px'
    } else {
      el.style.maxHeight = '0px'
    }
  }, [isOpen])

  return (
    <div className='w-full rounded-[16px] bg-white shadow-[4px_4px_54px_0_rgba(0,0,0,0.08)]'>
      {/* Header */}
      <div className='md:h-[116px] h-[171px] px-[28px] py-[24px] flex md:items-center flex-col md:flex-row justify-between'>
        {/* Left: icon + text */}
        <div className='flex items-center gap-[17px] min-w-0 flex-1'>
          {icon && (
            <ButtonWrapper>{icon}</ButtonWrapper>
          )}
          <div className='flex flex-col gap-[10px] min-w-0'>
            <span className='font-semibold text-[15px] leading-[100%] text-[#191919]'>
              {title}
            </span>
            <span className='font-normal text-[13px] leading-[100%] text-[#191919] truncate'>
              {summary}
            </span>
          </div>
        </div>

        {/* Right: action + chevron */}
        <div className='flex justify-between md:justify-end md:items-center md:gap-[18px] w-full md:w-auto mt-5 md:mt-0 ml-auto shrink-0'>
          {actionText && (
            <button
              type='button'
              className='inline-flex items-center gap-[6px] h-[38px] px-[14px] rounded-[8px] bg-[#F5F5F5] text-[#191919] text-[13px] font-medium leading-[150%] hover:bg-[#eee] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2'
            >
              <span className='font-medium text-[13px] leading-[150%] text-[#191919]'>
                {actionText}
              </span>
              <RiArrowRightUpLine className='text-[#262626]' size={20} />
            </button>
          )}

          {/* Chevron trigger (accordion button) */}
          <button
            type='button'
            id={`${panelId}-button`}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => setIsOpen((v) => !v)}
            className='grid place-items-center rounded-md p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2'
          >
            <RiArrowRightSLine
              className={`text-[#262626] transition-transform duration-200 cursor-pointer ${isOpen ? 'rotate-90' : 'rotate-0'}`}
              size={22}
            />
          </button>
        </div>
      </div>

      {/* Divider */}
      {showDivider && (
        <div
          className={`mx-[28px] h-px bg-[#EDEDED] ${isOpen ? 'block' : 'hidden'}`}
        />
      )}

      {/* Accordion panel */}
      <div
        id={panelId}
        ref={panelRef}
        role='region'
        aria-labelledby={`${panelId}-button`}
        className='overflow-hidden transition-all duration-300 ease-in-out'
        style={{ maxHeight: '0px' }} // initial collapsed
      >
        <div className='px-[28px] py-[20px]'>{children}</div>
      </div>
    </div>
  )
}
